import React, { useRef, useState, useCallback, memo, useEffect, useMemo } from 'react';
import { useSelector } from 'store';
import { reduxActions, useDispatch } from 'store';
import ChatWindow from 'components/containers/ChatWindow';
import { useChatState } from 'hooks/useChatState';
import classnames from 'classnames';

import styles from 'theme/components/containers/HorizontalChatContainer/HorizontalChatContainer.module.scss';

// Stałe dla ograniczeń wysokości okna horizontal
const MIN_HORIZONTAL_HEIGHT = 200;
const MAX_HORIZONTAL_HEIGHT = 700;
const ANIMATION_DURATION = 400; // ms

const HorizontalChatContainer: React.FC = memo(() => {
  const dispatch = useDispatch();
  const { chatHorizontalHeight, isChatHorizontal } = useSelector((state) => state.chat);

  const {
    isChatOpen,
    shouldShowAgentAI,
    closeChat,
    toggleDock,
    toggleHorizontal,
    toggleResizable,
    toggleFullScreen
  } = useChatState();
  
  // Refs dla elementów DOM
  const containerRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);

  // Stan lokalny dla płynnego resize'owania i animacji
  const [isResizing, setIsResizing] = useState(false);
  const [localHeight, setLocalHeight] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Rozpocznij proces zmiany rozmiaru
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  // Obsługa przesuwania myszy podczas zmiany rozmiaru
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      // Anuluj oczekujące RAF, aby zapobiec kolejkowaniu
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Użyj RAF do ograniczenia aktualizacji do 60fps
      rafIdRef.current = requestAnimationFrame(() => {
        const newHeight = e.clientY - (containerRef.current?.getBoundingClientRect().top || 0);

        const clampedHeight = Math.max(MIN_HORIZONTAL_HEIGHT, Math.min(MAX_HORIZONTAL_HEIGHT, newHeight));

        // Użyj lokalnego stanu podczas resize dla płynnej wydajności
        setLocalHeight(clampedHeight);
      });
    },
    [isResizing]
  );

  // Zakończ proces zmiany rozmiaru i zapisz wynik
  const handleMouseUp = useCallback(() => {
    // Anuluj oczekujące RAF
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // Zapisz końcową wysokość do Redux
    if (localHeight !== null) {
      dispatch(reduxActions.setChatHorizontalHeight(localHeight));
      setLocalHeight(null);
    }

    setIsResizing(false);
  }, [localHeight, dispatch]);

  // Obsługa globalnych eventów podczas zmiany rozmiaru
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMoveWithCursor = (e: MouseEvent) => {
      // Wymuś kursor row-resize podczas całego procesu przeciągania
      document.body.style.cursor = 'row-resize';
      handleMouseMove(e);
    };

    // Dodaj globalne listenery
    document.addEventListener('mousemove', handleMouseMoveWithCursor);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp); // Obsługa gdy kursor opuści okno przeglądarki
    
    // Wymuś kursor i style na całym dokumencie
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'auto';

    return () => {
      // Usuń listenery
      document.removeEventListener('mousemove', handleMouseMoveWithCursor);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);

      // Przywróć domyślne style
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      
      // Anuluj oczekujące RAF, aby zapobiec wyciekom pamięci
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Animacja przy otwieraniu chatu horizontal
  useEffect(() => {
    if (isChatHorizontal && isChatOpen) {
      setIsAnimating(true);
      // Usuń klasę animacji po zakończeniu animacji
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, ANIMATION_DURATION);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
    }
  }, [isChatHorizontal, isChatOpen]);

  // Oblicz aktualną wysokość kontenera
  const containerHeight = useMemo(() => {
    return localHeight !== null && isResizing ? localHeight : chatHorizontalHeight;
  }, [localHeight, isResizing, chatHorizontalHeight]);

  // Style dla overlay podczas resize'owania
  const resizeOverlayStyle = useMemo(
    () => ({
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      cursor: 'row-resize' as const,
      zIndex: 10000,
      pointerEvents: 'auto' as const,
      userSelect: 'none' as const,
      WebkitUserSelect: 'none' as const
    }),
    []
  );

  // Klasy CSS dla kontenera
  const containerClassName = useMemo(
    () => classnames(styles.horizontalChatContainer, {
      [styles.animating]: isAnimating
    }),
    [isAnimating]
  );

  // Nie renderuj, jeśli agent nie powinien być widoczny
  if (!shouldShowAgentAI('horizontal')) return null;

  return (
    <>
      {/* Overlay podczas zmiany rozmiaru - przechwytuje eventy myszy */}
      {isResizing && (
        <div style={resizeOverlayStyle} onMouseUp={handleMouseUp} />
      )}

      <div
        ref={containerRef}
        className={containerClassName}
        style={{ height: `${containerHeight}px` }}
      >
        <ChatWindow
          variant="docked"
          onCloseChat={closeChat}
          onToggleDock={toggleDock}
          onToggleHorizontal={toggleHorizontal}
          onToggleResizable={toggleResizable}
          onToggleFullScreen={toggleFullScreen}
          showNavigationBar
          showDragHandle={false}
          contentWidth={900}
        />

        {/* Handle do zmiany rozmiaru */}
        <div
          className={styles.resizeHandle}
          onMouseDown={handleMouseDown}
          title="Przeciągnij aby zmienić wysokość"
        />
      </div>
    </>
  );
});

HorizontalChatContainer.displayName = 'HorizontalChatContainer';

export default HorizontalChatContainer;

